import PopoverInfo from "@components/form/popover-info/PopoverInfo";
import CancellationTooltip from "@components/form/tooltips/CancellationTooltip";
import useOnboardingLabels from "@hooks/onboarding/labels/useOnboardingLabels";
import { useAsyncSelector } from "@hooks/useAsyncSelector";
import {
  getTextForInfo,
  parseRiskNumValueToLabel
} from "@page-components/onboarding/FinancialPosition/strategy/helpers";
import { EntriesItem } from "@page-components/onboarding/Summary/EntriesItem";
import { getConfirm } from "@page-components/onboarding/Summary/helpers";
import {
  currentDepotIDState,
  currentDepotInvestmentSettingState
} from "@states/current.depot.state";
import { depotDetailsState, depotsState } from "@states/depot.state";
import { SButton } from "@styled-components/form/SButtons";
import { SCustomDivider } from "@styled-components/SCustomDivider";
import { SH2, SSection } from "@styled-components/STags";
import useThemedModal from "@theme/hook/useThemedModal";

import ReallocationTooltip from "@src/components/layout/tooltips/ReallocationTooltip";
import formatLinebreaks from "@src/utils/format/formatLinebreaks";

import { ThemeContext } from "@src/pages/_app";
import useIsMobile from "@src/theme/hook/useIsMobile";
import { useRouter } from "next/router";
import { useContext, useEffect } from "react";
import { useSetRecoilState } from "recoil";
import { onModalFormOpen } from "../../Profile/helpers";
import { InvestmentModalForm } from "./InvestmentModalForm";
import { RiskModalForm } from "./RiskModalForm";

interface InvestmentSettingsProps {
  isUnderage: boolean;
}

export const InvestmentSettings: React.FunctionComponent<
  InvestmentSettingsProps
> = ({ isUnderage }) => {
  const { contextHolder, openModal } = useThemedModal();

  const [, depots] = useAsyncSelector(depotsState);

  const [, depotDetails] = useAsyncSelector(depotDetailsState);

  const [, investmentSettings] = useAsyncSelector(
    currentDepotInvestmentSettingState
  );

  const router = useRouter();

  const labels = useOnboardingLabels("dashboard");

  const { theme } = useContext(ThemeContext);

  const setCurrentDepotID = useSetRecoilState(currentDepotIDState);

  const isMobile = useIsMobile();

  // Opens the Investment Settings modal, with an optional initial confirmation dialog based on the `skipFirstDialog` flag.
  const openInvestmentSettingsModal = (skipFirstDialog?: boolean) => {
    if (skipFirstDialog) {
      onModalFormOpen(openModal, InvestmentModalForm, 900, {
        isUnderage
      })();
    } else {
      const modalInst = openModal("confirm", {
        ...labels.investmentSettingsModal.investmentSettingsConfirm,
        onOk: () => {
          onModalFormOpen(openModal, InvestmentModalForm, 900, {
            isUnderage
          })();
          modalInst.destroy();
        }
      });
    }
  };

  // Find the depot entity that matches the currently active depot, based on its ID.
  const currentDepotEntity = depots?.find(
    depot => depot.id === depotDetails?.id
  );

  // Find the depot specified by the user query parameter in the URL.
  // This is used to set or update the active depot when redirected from other pages.
  const depotFromQuery = depots?.find(
    depot => depot.userUri === router.query.forUser
  );
  useEffect(() => {
    if (
      router.query.forUser &&
      router.query.forUser !== currentDepotEntity?.userUri
    ) {
      setCurrentDepotID(depotFromQuery?.id);
      openInvestmentSettingsModal(true);
    }
  }, [router.isReady, router.query.forUser]);

  useEffect(() => {
    if (router.query.open && router.query.open === "risk-modal") {
      onModalFormOpen(openModal, RiskModalForm, undefined, {
        source: "dashboard",
        isUnderage
      })();
    }
  }, [router.isReady, router.query.open]);

  return (
    <SSection>
      <SH2 fontWeight={700} margin="0 0 16px" color={theme.palette.primary}>
        Anlagerisiko
      </SH2>

      {depotDetails && (
        <EntriesItem
          mobileLayout
          label="Aktuelles Anlagerisiko:"
          alignSelectionBottom
        >
          {parseRiskNumValueToLabel(depotDetails?.anlageRisiko ?? 15)} (
          {depotDetails.anlageRisiko}% max. Drawdown)
          <PopoverInfo
            text={getTextForInfo(
              parseRiskNumValueToLabel(depotDetails?.anlageRisiko ?? 15),
              isUnderage
            )}
            title={parseRiskNumValueToLabel(depotDetails?.anlageRisiko ?? 15)}
          />
        </EntriesItem>
      )}

      {depotDetails?.desiredAnlageRisiko && (
        <EntriesItem
          mobileLayout
          label={
            <>
              Mit der nächsten Reallokation
              <ReallocationTooltip />:
            </>
          }
          alignSelectionBottom
        >
          {parseRiskNumValueToLabel(
            depotDetails.desiredAnlageRisiko ?? depotDetails.anlageRisiko
          )}{" "}
          ({depotDetails?.desiredAnlageRisiko}% max. Drawdown)
          <PopoverInfo
            text={getTextForInfo(
              parseRiskNumValueToLabel(depotDetails.desiredAnlageRisiko),
              isUnderage
            )}
            title={parseRiskNumValueToLabel(depotDetails?.desiredAnlageRisiko)}
          />
        </EntriesItem>
      )}

      <SCustomDivider style={{ width: "86%", margin: "40px auto" }} />

      <EntriesItem
        mobileLayout
        label={
          isUnderage
            ? "Ich möchte für mein Kind nur in nachhaltige Anlageformen investieren:"
            : "Ich möchte nur in nachhaltige Anlageformen investieren:"
        }
        alignSelectionBottom
      >
        {getConfirm(investmentSettings?.onlySustainableCompanies)}
        <PopoverInfo
          title="Nachhaltige Anlageformen"
          text={
            <>
              Wir bewerten, in unserer nachhaltigen Variante, alle Investitionen
              in unserem Vermögensmanagement nach sogenannten ESG-Kriterien.
              Erfahren Sie mehr in unserer{" "}
              <a
                href="https://smavesto.de/files/32/Nachhaltigkeitsbezogene_Offenlegung_der_Smavesto_GmbH_06_2024_1.pdf"
                target="_blank"
                rel="noreferrer"
              >
                <u>Nachhaltigkeitsrichtlinie</u>
              </a>
              .
            </>
          }
        />
      </EntriesItem>

      <EntriesItem
        mobileLayout
        label={labels.investmentRisk.noForeignCurrency.label}
        alignSelectionBottom
      >
        {getConfirm(!investmentSettings?.noForeignCurrency)}
        <PopoverInfo
          title={labels.investmentRisk.noForeignCurrency.label}
          text={labels.investmentRisk.noForeignCurrency.info}
        />
      </EntriesItem>

      <EntriesItem
        mobileLayout
        label={labels.investmentRisk.noEmerginMarkets.label}
        alignSelectionBottom
      >
        {getConfirm(!investmentSettings?.noEmergingMarkets)}
        <PopoverInfo
          title={labels.investmentRisk.noEmerginMarkets.label}
          text={formatLinebreaks(labels.investmentRisk.noEmerginMarkets.info)}
        />
      </EntriesItem>

      <EntriesItem
        mobileLayout
        label={labels.investmentRisk.investInGold.label}
        alignSelectionBottom
      >
        {getConfirm(investmentSettings?.investInGold)}
        <PopoverInfo
          title={labels.investmentRisk.investInGold.label}
          text={formatLinebreaks(labels.investmentRisk.investInGold.info)}
        />
      </EntriesItem>

      <EntriesItem
        mobileLayout
        label={labels.investmentRisk.investInCrypto.label}
        alignSelectionBottom
      >
        {getConfirm(investmentSettings?.investInCrypto)}
        <PopoverInfo
          title={labels.investmentRisk.investInCrypto.label}
          text={formatLinebreaks(labels.investmentRisk.investInCrypto.info)}
        />
      </EntriesItem>

      <SSection
        display="flex"
        justifyContent="center"
        alignItems="center"
        margin="40px auto"
        flexWrap="wrap"
      >
        <CancellationTooltip>
          <SButton
            size="large"
            type="primary"
            style={{ margin: 12, width: isMobile ? "100%" : 240 }}
            onClick={() => openInvestmentSettingsModal(false)}
          >
            Präferenzen ändern
          </SButton>
        </CancellationTooltip>
        <CancellationTooltip>
          <SButton
            size="large"
            type="primary"
            style={{ margin: 12, width: isMobile ? "100%" : 240 }}
            onClick={() => {
              const modalInst = openModal("confirm", {
                ...labels.investmentSettingsModal.investmentRiskConfirm,
                onOk: () => {
                  onModalFormOpen(openModal, RiskModalForm, undefined, {
                    source: "dashboard",
                    isUnderage
                  })();
                  modalInst.destroy();
                }
              });
            }}
          >
            Anlagerisiko ändern
          </SButton>
        </CancellationTooltip>
      </SSection>

      {contextHolder}
    </SSection>
  );
};
